// @spec AUD-STR-046
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useAuditWebhook,
  useAuditWebhooks,
  useCreateAuditWebhook,
  useDeleteAuditWebhook,
  useRegenerateWebhookSecret,
  useTestWebhook,
  useUpdateAuditWebhook,
  useWebhookDeliveries,
} from '@/features/settings/audit/webhooks/api';
import {
  CreateWebhookDialog,
  WebhookDetailCard,
  WebhookList,
} from '@/features/settings/audit/webhooks/components';
import type {
  CreateAuditWebhookRequest,
  EventType,
} from '@/features/settings/audit/webhooks/types';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

// URL search params schema for validation
const auditWebhooksSearchSchema = z.object({
  webhookId: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/settings/audit-webhooks')({
  validateSearch: auditWebhooksSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.auditWebhooks'),
  }),
  component: AuditWebhooksPage,
});

/**
 * Audit webhooks configuration page.
 *
 * Features:
 * - List all audit webhooks for the organization
 * - Create new webhooks with generated signing secrets
 * - View webhook details and delivery statistics
 * - Test webhook connectivity
 * - Regenerate signing secrets
 * - Delete webhooks
 */
function AuditWebhooksPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate({ from: Route.fullPath });
  const { webhookId } = Route.useSearch();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteWebhookId, setDeleteWebhookId] = useState<string | null>(null);

  // API hooks
  const { data: webhooksData, isLoading: isLoadingWebhooks } = useAuditWebhooks();
  const { data: webhookDetail, isLoading: isLoadingDetail } = useAuditWebhook(webhookId);
  const { data: deliveriesData } = useWebhookDeliveries(webhookId, { pageSize: 10 });

  const createWebhook = useCreateAuditWebhook({
    onSuccess: () => {
      toast.success(t('settings:toast.webhookCreated'), {
        description: t('auditWebhooksPage.webhookCreatedDescription'),
      });
    },
    onError: (error) => {
      toast.error(t('settings:toast.failedToCreateWebhook'), {
        description: error.message,
      });
    },
  });

  const updateWebhook = useUpdateAuditWebhook(webhookId ?? '', {
    onSuccess: () => {
      toast.success(t('settings:toast.webhookUpdated'), {
        description: t('auditWebhooksPage.webhookUpdatedDescription'),
      });
    },
    onError: (error) => {
      toast.error(t('settings:toast.failedToUpdateWebhook'), {
        description: error.message,
      });
    },
  });

  const deleteWebhook = useDeleteAuditWebhook({
    onSuccess: () => {
      toast.success(t('settings:toast.webhookDeleted'), {
        description: t('auditWebhooksPage.webhookDeletedDescription'),
      });
      setDeleteWebhookId(null);
      // Navigate back to list if we were viewing the deleted webhook
      if (webhookId === deleteWebhookId) {
        void navigate({ search: {}, replace: true });
      }
    },
    onError: (error) => {
      toast.error(t('settings:toast.failedToDeleteWebhook'), {
        description: error.message,
      });
    },
  });

  const testWebhook = useTestWebhook(webhookId ?? '');
  const regenerateSecret = useRegenerateWebhookSecret(webhookId ?? '');

  const handleViewWebhook = (id: string) => {
    void navigate({ search: { webhookId: id }, replace: true });
  };

  const handleBackToList = () => {
    void navigate({ search: {}, replace: true });
  };

  const handleCreateWebhook = async (data: {
    name: string;
    endpointUrl: string;
    eventTypes: string[];
    domainFilters?: string[] | undefined;
    isEnabled: boolean;
  }) => {
    const request: CreateAuditWebhookRequest = {
      name: data.name,
      endpointUrl: data.endpointUrl,
      eventTypes: data.eventTypes as EventType[],
      domainFilters:
        data.domainFilters && data.domainFilters.length > 0 ? data.domainFilters : null,
      isEnabled: data.isEnabled,
    };
    const result = await createWebhook.mutateAsync(request);
    setShowCreateDialog(false);
    return { secret: result.secret };
  };

  const handleToggleEnabled = (enabled: boolean) => {
    updateWebhook.mutate({ isEnabled: enabled });
  };

  const handleTestWebhook = async () => {
    return testWebhook.mutateAsync();
  };

  const handleRegenerateSecret = async () => {
    return regenerateSecret.mutateAsync();
  };

  const handleDeleteWebhook = () => {
    if (deleteWebhookId) {
      deleteWebhook.mutate(deleteWebhookId);
    }
  };

  // Show detail view if webhookId is in URL
  if (webhookId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl">
          <WebhookDetailCard
            webhook={webhookDetail ?? null}
            deliveries={deliveriesData?.items ?? []}
            isLoading={isLoadingDetail}
            isTestingWebhook={testWebhook.isPending}
            isRegeneratingSecret={regenerateSecret.isPending}
            isTogglingEnabled={updateWebhook.isPending}
            onBackClick={handleBackToList}
            onToggleEnabled={handleToggleEnabled}
            onTestWebhook={handleTestWebhook}
            onRegenerateSecret={handleRegenerateSecret}
            onDeleteClick={() => {
              setDeleteWebhookId(webhookId);
            }}
          />
        </div>

        <AlertDialog
          open={!!deleteWebhookId}
          onOpenChange={() => {
            setDeleteWebhookId(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('auditWebhooksPage.deleteTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('auditWebhooksPage.deleteDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('buttons.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteWebhook}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t('buttons.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // Show list view
  return (
    <div className="p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('auditWebhooksPage.title')}</h1>
          <p className="mt-1 text-muted-foreground">{t('auditWebhooksPage.description')}</p>
        </header>

        <WebhookList
          webhooks={webhooksData?.items ?? []}
          isLoading={isLoadingWebhooks}
          onCreateClick={() => {
            setShowCreateDialog(true);
          }}
          onViewClick={handleViewWebhook}
          onDeleteClick={(id) => {
            setDeleteWebhookId(id);
          }}
        />

        <CreateWebhookDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          onSubmit={handleCreateWebhook}
          isSubmitting={createWebhook.isPending}
        />

        <AlertDialog
          open={!!deleteWebhookId}
          onOpenChange={() => {
            setDeleteWebhookId(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('auditWebhooksPage.deleteTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('auditWebhooksPage.deleteDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('buttons.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteWebhook}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t('buttons.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
